import { hasValue } from "@itk/typescript-utils";
import moment, { Moment } from "moment";

export const formatIntegerWithoutThousandSep = (value: unknown) => {
  return value;
};

export const convertToDateOnly = <T extends Date | null | undefined>(date: T): T extends Date ? string : string | undefined => {
  if (!hasValue(date)) return undefined as unknown as string;

  return moment(date).format("YYYY-MM-DD");
};

export const convertToDateAndTime = <T extends Date | null | undefined>(date: T): T extends Date ? string : string | undefined => {
  if (!hasValue(date)) return undefined as unknown as string;

  return moment(date).format("YYYY-MM-DDTHH:mm");
};

export const convertToTimeOnly = (date: Date | undefined | null): string | undefined => {
  if (!hasValue(date)) return undefined;

  return moment(date).format("HH:mm");
};

export const formatToDateOnly = (date: Date | undefined | null, format = "DD.MM.YYYY"): string | undefined => {
  if (!hasValue(date)) return undefined;

  return moment(date).format(format);
};

export const formatToTimeWithoutSecondsOnly = (date: Date | undefined | null): string | undefined => {
  if (!hasValue(date)) return undefined;

  return moment(date).format("HH:mm");
};

export const formatToTimeWithSecondsOnly = (date: Date | undefined | null): string | undefined => {
  if (!hasValue(date)) return undefined;

  return moment(date).format("HH:mm:ss");
};

export const formatToWeekdayOnly = (date: Date | undefined | null): string | undefined => {
  if (!hasValue(date)) return undefined;

  return moment(date).format("dddd");
};

export const formatToWeekNumber = (date: Date | undefined | null): number | undefined => {
  if (!hasValue(date)) return undefined;

  return moment(date).week();
};

export const formatToDateAndTime = (date: Date | undefined | null): string | undefined => {
  if (!hasValue(date)) return undefined;
  return moment(date).format("DD.MM.YYYY HH:mm");
};

export const formatToDateAndTimeAndSeconds = (date: Date | undefined | null): string | undefined => {
  if (!hasValue(date)) return undefined;
  return moment(date).format("DD.MM.YYYY HH:mm:ss");
};

export const formatDateToLongDateOnly = (date: Date | undefined | null): string | undefined => {
  if (!hasValue(date)) return undefined;
  return moment(date).format("LL");
};
export const formatDateToLongDateAndTime = (date: Date | undefined | null): string | undefined => {
  if (!hasValue(date)) return undefined;
  return moment(date).format("LLL");
};

export const formatToDateAndTimeIfAvailable = (date: Date | undefined | null): string | undefined => {
  if (!hasValue(date)) return undefined;

  if (date.getHours() === 0 && date.getMinutes() === 0 && date.getSeconds() === 0) {
    return formatToDateOnly(date);
  } else {
    return formatToDateAndTime(date);
  }
};

export const todayMinusXDays = (daysToSubtract: number): Date => {
  return moment().subtract(daysToSubtract, "days").toDate();
};
export const convertStringDateToDate = (date: string): Date => {
  return moment(date).toDate();
};

export const formatToDateWithSlash = (date: string | undefined): string | undefined => {
  if (!hasValue(date)) return undefined;
  const dateValue = moment(date);
  const formattedDate = dateValue.format("DD/MM/YY");
  return formattedDate;
};
export const datePlusXDays = (daysToAdd: number, date?: Date | null | undefined): Date => {
  if (!hasValue(date)) return moment().add(daysToAdd, "days").toDate();
  return moment(date).add(daysToAdd, "days").toDate();
};

export const calculateDuration = (fromTime: Date | undefined | null, toTime: Date | undefined | null, breakMinutes: number | undefined | null = 0): number | undefined => {
  if (!hasValue(fromTime) || !hasValue(toTime)) return undefined;

  if (toTime <= fromTime) {
    toTime = moment(toTime).add(1, "day").toDate();
  }

  const durationInHours = moment.duration(moment(toTime).diff(moment(fromTime))).asHours();
  const adjustedDuration = durationInHours - (breakMinutes || 0) / 60;

  return adjustedDuration;
};

export const isSameDay = (date: Date | undefined | null): boolean | undefined => {
  if (!hasValue(date)) return undefined;
  return moment().isSame(date, "day");
};

export const addHoursMinutesAndSeconds = (hours: number, minutes: number, seconds = 0, date?: Date): Date => {
  const targetDate = date ? moment(date) : moment();
  targetDate.set({ hour: hours, minute: minutes, second: seconds });
  return targetDate.toDate();
};

export const getStartAndEndWeekDates = (weekNumber: number, year: number) => {
  const start = moment().year(year).week(weekNumber).startOf("week").format("DD.MM.YYYY");
  const end = moment().year(year).week(weekNumber).endOf("week").format("DD.MM.YYYY");

  return { start, end };
};
export const formatToWeekdayAndDate = (date: Date | undefined | null): string | undefined => {
  if (!hasValue(date)) return undefined;
  return moment(date).format("ddd DD.MM.YYYY");
};

export const getDateOnlyWithZeroTime = (date: Date) => {
  return moment(date).set({ hour: 0, minute: 0, second: 0 });
};

export const combineDateAndTime = (dateString: Date, timeString: Date) => {
  const dateValue = moment(dateString);
  const finalTime = moment(timeString);
  return dateValue.set({
    hour: finalTime.hours(),
    minute: finalTime.minutes(),
    second: finalTime.seconds()
  });
};

export const formatTimeStringToDate = (timeString: string) => {
  return moment(timeString, "HH:mm").toDate();
};

export const calculateHourDifference = (fromDateTime: Date, toDateTime: Date) => {
  const duration = moment.duration(moment(toDateTime).diff(moment(fromDateTime)));
  return duration.asHours();
};

export const combineDateAndTimeNull = (dateString: Date, timeString: Date | null) => {
  const dateValue = moment(dateString);
  const finalTime = moment(timeString);
  return dateValue.set({
    hour: finalTime.hours(),
    minute: finalTime.minutes(),
    second: finalTime.seconds()
  });
};
export const compareDates = (
  fromDate: Date | undefined | null,
  toDate: Date | undefined | null,
  comparisonType: "isBefore" | "isSameOrBefore" | "isAfter" | "isSameOrAfter" | "isSame",
  includeTime = false
): boolean | undefined => {
  if (!fromDate || !toDate) return undefined;

  const fromDateOnly = includeTime ? moment(fromDate) : moment(fromDate).startOf("day");
  const toDateOnly = includeTime ? moment(toDate) : moment(toDate).startOf("day");

  switch (comparisonType) {
    case "isBefore":
      return fromDateOnly.isBefore(toDateOnly);
    case "isSameOrBefore":
      return fromDateOnly.isSameOrBefore(toDateOnly);
    case "isAfter":
      return fromDateOnly.isAfter(toDateOnly);
    case "isSameOrAfter":
      return fromDateOnly.isSameOrAfter(toDateOnly);
    case "isSame":
      return fromDateOnly.isSame(toDateOnly);
    default:
      return undefined;
  }
};

export const calculateMinutes = (fromDateTime: Moment, toDateTime: Moment) => {
  const duration = moment.duration(toDateTime.diff(fromDateTime));
  const minutes = duration.asHours() * 60;
  return minutes;
};

export const notExceedLimit24Hours = (fromDateTime: Moment, currentDate: Moment) => {
  const duration = moment.duration(currentDate.diff(fromDateTime));
  const minutes = duration.asHours() * 60;
  if (minutes < 1440) {
    return true;
  }
  return false;
};

export const getWeekdaywithLocal = (date: Moment | undefined | null, noOfLetters = 0, locale: string | undefined) => {
  if (!hasValue(date)) return undefined;

  let weekday = date.format("dddd");
  if (hasValue(locale)) weekday = date.locale(locale).format("dddd");
  const capitalisedDay = weekday.charAt(0).toUpperCase() + weekday.slice(1);
  if (noOfLetters > 0) {
    return capitalisedDay.substring(0, noOfLetters);
  }
  return capitalisedDay;
};

export const displayDate = (date: Date, languageCode: string) => {
  const dateFormat = moment(date);
  const dayName = getWeekdaywithLocal(moment(date), 3, languageCode) + ". ";
  return dayName + dateFormat.format("DD-MM-YYYY").toUpperCase();
};
